import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { Timer } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { useSocketEvent } from '../hooks/useSocketEvent';
import { requestGetRunningTimers } from '../redux/sagas/requests/time';
import RunningTimer from '../components/RunningTimer';

const TimerList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const TimerItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
        borderBottom: 'none',
    },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
    width: 36,
    height: 36,
    marginRight: theme.spacing(1.5),
    backgroundColor: theme.palette.primary.main,
    fontSize: theme.typography.size.md,
}));

const TimerContent = styled(Box)({
    flex: 1,
    minWidth: 0,
});

const UserName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
}));

const TaskName = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(Timer)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const formatDuration = (startTime) => {
    if (!startTime) return '0:00';

    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now - start;
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
};

const AllRunningTimersWidget = ({ widgetId, config = {}, onRemove }) => {
    const { data: runningTimers, isLoading, error, mutate } = useFetchQueue(widgetId, {
        apiCall: () => requestGetRunningTimers(),
        cacheKey: 'running-timers-all',
        cacheTtl: 30000, // 30 second cache for timers
        transform: (response) => response?.timers || response || [],
    });

    useSocketEvent('timer:started', (timer) => mutate((prev) => {
        const list = prev || [];
        if (list.some((t) => t._id === timer._id)) return list;
        return [timer, ...list];
    }));

    useSocketEvent('timer:stopped', ({ _id }) => mutate((prev) => (prev || []).filter((t) => t._id !== _id)));

    const hasData = (runningTimers || []).length > 0;

    return (
        <BaseWidget
            title="Running Timers"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasData ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">
                        No active timers
                    </Typography>
                </EmptyState>
            ) : (
                <TimerList>
                    {(runningTimers || []).map((item, index) => {
                        const userName = item.user?.fullName ||
                            (item.user?.first_name && item.user?.last_name
                                ? `${item.user.first_name} ${item.user.last_name}`
                                : 'Unknown');
                        const itemName = item.task?.name || item.project?.name || 'Unknown';
                        const projectName = item.task?.name ? item.project?.name : null;
                        const startedAt = item.startedAt || item.timer?.startedAt;

                        return (
                            <TimerItem key={item._id || index}>
                                <UserAvatar>
                                    {getInitials(userName)}
                                </UserAvatar>
                                <TimerContent>
                                    <UserName>{userName}</UserName>
                                    <TaskName>{itemName}</TaskName>
                                    {projectName && <TaskName>{projectName}</TaskName>}
                                </TimerContent>
                                <Typography variant="h5"><RunningTimer startTime={startedAt} /></Typography>
                            </TimerItem>
                        );
                    })}
                </TimerList>
            )}
        </BaseWidget>
    );
};

export default AllRunningTimersWidget;
